import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";

import AmountField from "../../Components/Form Fields/amount-field";
import PrimaryButton from "../../Components/Buttons/primary-button";

import renderInstantTransferIneligible from "../../Organisms/Modals/Cards/instant-transfer-ineligible";
import renderAstraSignUp from "../../Organisms/Modals/Cards/astra-sign-up";
import renderStatusModal from "../../Organisms/Modals/status-modal";
import renderInstantTransferLimitReached from "../../Organisms/Modals/Cards/instant-transfer-limit-reached";
import renderSelectConnectedAccounts from "../../Organisms/Modals/Bank Account/select-connected-accounts";
import renderSelectDebitCards from "../../Organisms/Modals/Cards/select-debit-cards";
import renderAuthorizeTransfer from "../../Organisms/Modals/Transfer/authorize-transfer";

import {
  useActivities,
  useBankingLimits,
  useConnectedAccounts,
  useConnectedAccountSelection,
  useDeals,
  useStakeDebit,
} from "../../CustomHooks/Queries";
import {
  useInitiatePlaidWithdrawal,
  useCreateAccountToCardTransferRoutine,
  useCreateAccountToCardTransferRoutineWithoutAuthToken,
} from "../../CustomHooks/Mutations";

import useAstra from "../../CustomHooks/useAstra";

import withFormProvider from "../../HOC/withFormProvider";

import { useModalContext } from "../../Contexts/ModalContext";
import { useAppUtilityContext } from "../../Contexts/AppUtilityContext";
import { useAdminStateSafe } from "../../Contexts/AdminStateContext";

import { getTransferRoutinePayload } from "../../Utils/api-payload-generators";
import {
  dispatchErrorToast,
  formatCurrency,
  getTransferTypes,
  isSVDApproved,
  getSelectedAstraCard,
  isSelectedTransferMethodInstant,
  isSelectedTransferMethodBank,
  getAstraCardsWithSystemCardFiltered,
} from "../../Utils/helpers";
import { FailedTransferStatusModalProps, SuccessfulTransferStatusModalProps } from "../../Utils/constants";
import { getLastFour } from "../../Utils/pii";

import { ChevronLeft } from "../../Assets/Icons";

function CashOut() {
  const modalContext = useModalContext();

  const navigate = useNavigate();

  const { appUtilityProps, setAppUtilityProps } = useAppUtilityContext();

  const [selectedTransferType, setSelectedTransferType] = useState("ach");

  const { watch, handleSubmit } = useFormContext();

  const { isLoading: isLoadingDeals, data: deals } = useDeals();

  const { isLoading: isLoadingStakeDebit, data: stakeDebit } = useStakeDebit();

  const { isLoading: isLoadingBankingLimits, data: bankingLimits } = useBankingLimits();

  const { isLoading: isLoadingActivities, data: activitiesData } = useActivities();

  const {
    isCreatingUserIntent: isAstraCreatingUserIntent,
    isLoadingRoutesCapabilities,
    isCreatingTrustedAuthenticationToken,
    isLoadingAstraCards,
    isInstantTransferIneligible,
    openAstraPortal,
    astraDebitCards,
  } = useAstra();

  const { isLoading: isLoadingConnectedAccounts, data: connectedAccounts } = useConnectedAccounts();

  const { isLoading: isLoadingConnectedAccountSelection, data: connectedAccountSelection } =
    useConnectedAccountSelection();

  const isAstraAccountConnected = useMemo(
    () => getAstraCardsWithSystemCardFiltered(astraDebitCards, stakeDebit).length > 0,
    [astraDebitCards, stakeDebit],
  );

  const IsSVDApproved = useMemo(() => {
    return isSVDApproved(stakeDebit?.data);
  }, [stakeDebit?.data]);

  const selectedAccount = useMemo(
    () =>
      connectedAccounts?.data?.data?.accounts?.find(
        (account) =>
          account.account_id === appUtilityProps?.selectedAccountId ?? connectedAccountSelection?.data?.data?.selected,
      ) ??
      connectedAccounts?.data?.data?.accounts?.[0] ??
      null,
    [
      appUtilityProps?.selectedAccountId,
      connectedAccounts?.data?.data?.accounts,
      connectedAccountSelection?.data?.data?.selected,
    ],
  );

  const selectedAstraCard = useMemo(
    () => getSelectedAstraCard(astraDebitCards, stakeDebit, appUtilityProps?.selectedCardId),
    [astraDebitCards, stakeDebit, appUtilityProps?.selectedCardId],
  );

  const SVDApproved = useMemo(() => {
    return isSVDApproved(stakeDebit?.data);
  }, [stakeDebit?.data]);

  const instantAccountDescription = useMemo(() => {
    return !!selectedAstraCard ? `${selectedAstraCard?.card_company} (...${selectedAstraCard?.last_four_digits})` : "";
  }, [selectedAstraCard]);

  const accountDescription = useMemo(() => {
    return !!selectedAccount ? `${selectedAccount?.name} (...${selectedAccount?.mask})` : "";
  }, [selectedAccount]);

  const dailyCashOutLimit = useMemo(() => {
    let dailyLimit;
    if (SVDApproved) {
      dailyLimit = (bankingLimits?.daily_cash_out_limit ?? 0) / 100;
    } else {
      dailyLimit = deals?.data?.content?.transfer_out_limit ?? 1000;
    }
    return dailyLimit;
  }, [bankingLimits, deals, SVDApproved]);

  const adminState = useAdminStateSafe();
  const isMockInstantOff = adminState?.isMockMode && adminState?.selInstantCashOut === "ico-off";

  const transferTypes = useMemo(() => {
    const types = getTransferTypes(deals?.data);
    if (isMockInstantOff) return types.filter((t) => t.type !== "instant");
    return types;
  }, [deals, isMockInstantOff]);

  const hasInstantOption = useMemo(
    () => transferTypes.some((t) => t.type === "instant"),
    [transferTypes],
  );

  const onWithDrawSuccess = () => {
    navigate("/home");
    renderStatusModal(modalContext, {
      ...SuccessfulTransferStatusModalProps,
      description: isSelectedTransferMethodInstant(selectedTransferType)
        ? `Your transfer is expected to arrive in a few minutes.`
        : `Your transfer is expected to arrive on ${deals?.data?.content?.transaction_clearing_date}.`,
      onCTAButtonClick: () => modalContext.onModalClose(),
    });
  };

  const onWithDrawError = (errorProps = {}) =>
    renderStatusModal(modalContext, {
      ...FailedTransferStatusModalProps,
      ...errorProps,
      onCTAButtonClick: modalContext.onModalClose,
    });

  const onInstantWithDrawError = (err) => {
    if (err?.response?.status === 429) renderInstantTransferLimitReached(modalContext);
    else onWithDrawError();
  };

  const { isLoading: isInitiatingPlaidWithdrawal, mutate: InitiatePlaidWithdrawal } = useInitiatePlaidWithdrawal({
    onSuccess: () => onWithDrawSuccess(),
    onError: (err) => onWithDrawError(err?.response?.status === 422 ? { description: err?.response?.data?.error } : {}),
  });

  const { isLoading: isCreatingAccountToCardRoutine, mutate: CreateAccountToCardRoutine } =
    useCreateAccountToCardTransferRoutine({
      onSuccess: () => onWithDrawSuccess(),
      onError: onInstantWithDrawError,
    });

  const { isLoading: isCreatingAccountToCardRoutineWithoutAuth, mutate: CreateAccountToCardRoutineWithoutAuth } =
    useCreateAccountToCardTransferRoutineWithoutAuthToken({
      onSuccess: () => onWithDrawSuccess(),
      onError: () => onWithDrawError(),
    });

  const cashAmount = parseFloat(watch("cash-amount")?.unmasked ?? "");

  const disableSubmitButton = isNaN(cashAmount) || cashAmount === 0;

  useEffect(() => {
    if (isSelectedTransferMethodInstant(selectedTransferType) && isInstantTransferIneligible) {
      setSelectedTransferType("ach");
    }
  }, [selectedTransferType, isInstantTransferIneligible]);

  const onSubmit = () => {
    if (
      (isSelectedTransferMethodBank(selectedTransferType) && !selectedAccount) ||
      (isSelectedTransferMethodInstant(selectedTransferType) && !selectedAstraCard)
    ) {
      dispatchErrorToast(
        isSelectedTransferMethodInstant(selectedTransferType)
          ? "Please add a debit card to proceed"
          : "Please add a bank account to proceed",
        {
          alignToAppContent: true,
          key: isSelectedTransferMethodInstant(selectedTransferType) ? "noAstraDebitCard" : "noConnectedAccount",
        },
      );
    } else if (dailyCashOutLimit > 0 && cashAmount > dailyCashOutLimit) {
      dispatchErrorToast("Amount cannot exceed the daily limit.", {
        alignToAppContent: true,
        key: "amountExceedsThreshold",
      });
    } else if (
      cashAmount >
      (IsSVDApproved
        ? (stakeDebit?.data?.data?.available ?? 0) / 100
        : (activitiesData?.data?.data?.currentBalance ?? 0))
    ) {
      dispatchErrorToast("Amount exceeds what you have available to transfer.", {
        alignToAppContent: true,
        key: "amountExceedsThreshold",
      });
    } else {
      renderAuthorizeTransfer(modalContext, {
        stake_account_number_last4: getLastFour(stakeDebit?.data?.data?.account_number),
        stake_routing_number: stakeDebit?.data?.data?.routing_number,
        ...(isSelectedTransferMethodInstant(selectedTransferType) ? { selectedAstraCard } : { selectedAccount }),
        clearingTransactionDate: deals?.data?.content?.transaction_clearing_date,
        amount: cashAmount,
        isCashOut: true,
        AuthorizeCallback: () => {
          if (isSelectedTransferMethodInstant(selectedTransferType)) {
            const request = getTransferRoutinePayload({
              destinationCard: selectedAstraCard,
              amount: cashAmount,
            });
            if (SVDApproved) {
              CreateAccountToCardRoutine(request);
            } else {
              CreateAccountToCardRoutineWithoutAuth(request);
            }
          } else {
            InitiatePlaidWithdrawal({
              account_name: selectedAccount.name,
              account_id: selectedAccount?.account_id,
              amount: cashAmount,
            });
          }
        },
      });
    }
  };

  const handleChangeTransferType = (selectedType) => {
    if (isSelectedTransferMethodInstant(selectedType) && isInstantTransferIneligible) {
      renderInstantTransferIneligible(modalContext);
      return;
    }

    if (isSelectedTransferMethodInstant(selectedType) && !isAstraAccountConnected) {
      setTimeout(() => renderAstraSignUp(modalContext, openAstraPortal), 750);
    }

    setSelectedTransferType(selectedType);
    setAppUtilityProps((p) => ({
      ...p,
      preferredTransferType: selectedType,
    }));
  };

  const handleSelectPaymentEntity = () => {
    if (isSelectedTransferMethodInstant(selectedTransferType)) {
      if (isAstraAccountConnected) {
        renderSelectDebitCards(modalContext);
      } else {
        renderAstraSignUp(modalContext, openAstraPortal);
      }
    } else {
      renderSelectConnectedAccounts(modalContext);
    }
  };

  const feeLabel = useMemo(() => {
    const feePercentage = deals?.data?.data?.currentDeal?.INSTANT_TRANSFER_OUT_OF_STAKE_FEE_RATE ?? 0;

    if (isSelectedTransferMethodInstant(selectedTransferType) && isNaN(cashAmount)) {
      return `${(feePercentage * 100).toFixed(2)}%`;
    } else if (isSelectedTransferMethodInstant(selectedTransferType)) {
      return formatCurrency(cashAmount * feePercentage);
    } else {
      return "Free";
    }
  }, [deals, cashAmount, selectedTransferType]);

  const cashAmountIncludingFees = useMemo(() => {
    const feePercentage = deals?.data?.data?.currentDeal?.INSTANT_TRANSFER_OUT_OF_STAKE_FEE_RATE ?? 0;

    if (isSelectedTransferMethodInstant(selectedTransferType)) {
      if (isNaN(cashAmount)) {
        return 0;
      }

      return cashAmount + cashAmount * feePercentage;
    } else {
      return cashAmount;
    }
  }, [cashAmount, selectedTransferType, deals]);

  const transferAmountLabel = "Cash Out";

  const isLoading =
    isLoadingDeals ||
    isLoadingStakeDebit ||
    isLoadingActivities ||
    isLoadingConnectedAccounts ||
    isLoadingConnectedAccountSelection ||
    isLoadingAstraCards ||
    isLoadingBankingLimits;

  const [animReady, setAnimReady] = useState(false);
  const hasTriggeredAnim = useRef(false);

  useEffect(() => {
    if (!isLoading && !hasTriggeredAnim.current) {
      hasTriggeredAnim.current = true;
      requestAnimationFrame(() => setAnimReady(true));
    }
  }, [isLoading]);

  const isSettingUpAstra =
    isAstraCreatingUserIntent || isLoadingRoutesCapabilities || isCreatingTrustedAuthenticationToken;

  const isSubmitting =
    isInitiatingPlaidWithdrawal || isCreatingAccountToCardRoutine || isCreatingAccountToCardRoutineWithoutAuth;

  const availableBalance = IsSVDApproved
    ? (stakeDebit?.data?.data?.available ?? 0) / 100
    : (activitiesData?.data?.data?.currentBalance ?? 0);

  const toEntityName = useMemo(() => {
    if (isSelectedTransferMethodInstant(selectedTransferType) && selectedAstraCard) {
      return selectedAstraCard.card_company;
    }
    if (isSelectedTransferMethodBank(selectedTransferType) && selectedAccount) {
      return selectedAccount.name;
    }
    return null;
  }, [selectedTransferType, selectedAstraCard, selectedAccount]);

  const toEntityMask = useMemo(() => {
    if (isSelectedTransferMethodInstant(selectedTransferType) && selectedAstraCard) {
      return `...${selectedAstraCard.last_four_digits}`;
    }
    if (isSelectedTransferMethodBank(selectedTransferType) && selectedAccount) {
      return `...${selectedAccount.mask}`;
    }
    return null;
  }, [selectedTransferType, selectedAstraCard, selectedAccount]);

  return (
    <div className={`cash-out${animReady ? " anim-ready" : ""}`}>
      <section className="card-section-wrapper">
        <div className="max-width-container">
          <div className="card-section">
            <div className="section-header">
              <button className="go-back-button" onClick={navigate.bind(this, -1)} aria-label="Go back">
                <ChevronLeft />
              </button>
              <h3 className="section-title">Cash Out</h3>
            </div>
            {isLoading ? (
              <div className="loading-content">
                <CircularProgress />
              </div>
            ) : (
              <form className="content" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <div className="top-container">
                  <AmountField />
                  <span className="co-available-balance-pill">
                    {formatCurrency(availableBalance)} available
                  </span>
                  {!!dailyCashOutLimit && !isNaN(cashAmount) && cashAmount > dailyCashOutLimit && (
                    <span className="amount-usage-label">{formatCurrency(dailyCashOutLimit, 0)} daily limit</span>
                  )}
                  {isSelectedTransferMethodInstant(selectedTransferType) && (
                    <span className="max-no-of-transfers-badge mt-2">2 instant transfers per day</span>
                  )}
                </div>

                {hasInstantOption && (
                  <div className="co-speed-selection">
                    {transferTypes.map((transferType) => (
                      <button
                        key={transferType.type}
                        className={`co-speed-card ${transferType.type}${
                          transferType.type === selectedTransferType ? " selected" : ""
                        }`}
                        type="button"
                        onClick={handleChangeTransferType.bind(this, transferType.type)}
                        disabled={isSelectedTransferMethodInstant(transferType.type) && isSettingUpAstra}
                      >
                        <span className="co-speed-icon">
                          <transferType.Icon className="icon" />
                        </span>
                        <span className="co-speed-title">{transferType.title}</span>
                        {isSelectedTransferMethodInstant(transferType.type) && isSettingUpAstra ? (
                          <CircularProgress />
                        ) : (
                          <span className="co-speed-desc">{transferType.description}</span>
                        )}
                        {isSelectedTransferMethodInstant(transferType.type) && isInstantTransferIneligible && (
                          <span className="co-restricted-badge">Restricted</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                <div className="bottom-container">
                  <div className="details-card">
                    <div className="detail-row">
                      <span className="detail-label">From</span>
                      <span className="detail-value">Stake</span>
                    </div>
                    <div className="detail-divider" />
                    <button type="button" className="detail-row co-to-row" onClick={handleSelectPaymentEntity}>
                      <span className="detail-label">To</span>
                      <span className={`detail-value${!toEntityName ? " co-link-text" : ""}`}>
                        {toEntityName ? (
                          <>
                            {toEntityName} ({toEntityMask})
                          </>
                        ) : (
                          isSelectedTransferMethodInstant(selectedTransferType)
                            ? "Link a card"
                            : "Link a bank"
                        )}
                        <svg className="co-row-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M6 4l4 4-4 4" stroke="#8B8FA3" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    </button>
                    <div className="detail-divider" />
                    <div className="detail-row">
                      <span className="detail-label">Fee</span>
                      <span className="detail-value fee-value">{feeLabel}</span>
                    </div>
                    <div className="detail-divider" />
                    <div className="detail-row timing-row">
                      <div className="info">
                        {isSelectedTransferMethodInstant(selectedTransferType)
                          ? "Transfer speed depends on your bank and could take up to 30 minutes."
                          : "Standard transfers take 1\u20133 business days. Requests after 3 PM ET or on weekends/holidays are processed the next business day."}
                      </div>
                    </div>
                  </div>
                  <PrimaryButton type="submit" disabled={disableSubmitButton || isSubmitting}>
                    {transferAmountLabel} {isSubmitting && <CircularProgress />}
                  </PrimaryButton>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default withFormProvider(CashOut);
